import React, { useRef, useState } from "react";
import emailjs from "emailjs-com";
import { ChevronDown, ChevronUp, CheckCircle, Briefcase } from "lucide-react";
import {
  HiAcademicCap,
  HiCalendar,
  HiMail,
  HiOfficeBuilding,
} from "react-icons/hi";
import { HiMapPin } from "react-icons/hi2";
import WhyJoinTissa from "./WhyJoinTissa";
import HeroSection from "../common/HeroSection";

const IndiaJobOpening = () => {
  const [openJobId, setOpenJobId] = useState(null);
  const jobRefs = useRef({});

  const toggleDetails = (jobId) => {
    setOpenJobId((prevId) => {
      const newId = prevId === jobId ? null : jobId;
      setTimeout(() => {
        if (newId !== null) {
          jobRefs.current[newId]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
      return newId;
    });
  };

  const jobList = [
    {
      id: "BDEIT26",
      title: "IT Business Development Executive (BDE)",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education: "Bachelor's degree or equivalent (preferred)",
      description:
        "Entry-level to mid-level role focused on IT sales, lead generation, and business development. Ideal for candidates who want to grow in IT sales with opportunities to learn, build client relationships, and contribute to company growth.",
      requirements:
        "0–3 years of experience in sales, business development, or a related field. Freshers with strong communication skills are encouraged to apply.",
      jobDescription:
        "As an IT Business Development Executive at TISSA Technology, you will identify new business opportunities, engage with potential clients, and contribute to the company’s growth by executing effective outreach strategies. You will work closely with internal teams to align client needs with company offerings while consistently meeting sales targets.",
      responsibilities: [
        "Identify and reach out to potential clients through cold calling, networking, and email campaigns",
        "Execute email marketing campaigns with effective messaging strategies",
        "Build and maintain strong client relationships by understanding business needs",
        "Collaborate with internal teams to align business development with company goals",
        "Meet and exceed sales targets through proactive follow-ups and engagement",
        "Provide insights on market trends, competitor activities, and client requirements",
      ],
      skills: [
        "Cold Calling",
        "Lead Generation",
        "Email Marketing Tools",
        "Sales Communication",
        "Client Relationship Management",
        "Market Research",
        "Basic IT Products/Services Knowledge",
        "Negotiation",
        "Presentation Skills",
      ],
      status: "Active",
    },
    {
      id: "DMEIT26",
      title: "Digital Marketing Executive",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education: "Bachelor's degree or equivalent (preferred)",
      description:
        "Digital Marketing Executive role focused on B2B technology marketing, lead generation, and campaign optimization. This position is ideal for individuals passionate about driving impactful digital strategies across multiple channels to strengthen brand presence and generate qualified leads.",
      requirements:
        "1–3 years of experience in B2B digital marketing with proven expertise in lead generation, campaign optimization, and digital tools.",
      jobDescription:
        "As a Digital Marketing Executive at TISSA Technology, you will plan, execute, and optimize marketing initiatives across multiple channels, including PPC, SEO, email, and social media. You will work closely with cross-functional teams to drive lead generation, enhance online presence, and contribute to brand growth through innovative digital strategies.",
      responsibilities: [
        "Develop and execute effective lead generation strategies to drive B2B growth",
        "Manage and optimize PPC/SEM campaigns across LinkedIn and Google Ads",
        "Create and run email marketing campaigns using automation tools for lead nurturing",
        "Drive content marketing initiatives such as blogs, case studies, landing pages, and video scripts",
        "Implement SEO/SEM best practices (technical and on-page) to improve search visibility",
        "Manage and grow brand presence across LinkedIn, X, and YouTube",
        "Monitor and analyze performance using Google Analytics, HubSpot, and other tools, and prepare reports",
      ],
      skills: [
        "Lead Generation",
        "PPC Campaign Management",
        "Google Ads",
        "LinkedIn Ads",
        "SEO/SEM",
        "Email Marketing Automation",
        "Content Marketing",
        "Social Media Management",
        "Google Analytics",
        "HubSpot",
        "Photoshop",
        "CorelDRAW",
        "Illustrator",
        "Canva",
      ],
      status: "Active",
    },
    {
      id: "ODODEV26",
      title: "Odoo Developer",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education: "Bachelor's degree or equivalent (preferred)",
      description:
        "Experienced Odoo Developer role focused on designing, customizing, and integrating Odoo applications to deliver scalable business solutions. The role involves working on diverse projects, ensuring performance optimization, and collaborating with cross-functional teams.",
      requirements:
        "3–6 years of proven experience in Odoo development & customization with strong expertise in Python, PostgreSQL, and the Odoo framework.",
      jobDescription:
        "As an Odoo Developer at TISSA Technology, you will be responsible for developing and customizing Odoo modules, integrating third-party services, and ensuring system scalability. You will collaborate with consultants and developers to deliver quality solutions while staying updated with Odoo best practices and framework updates.",
      responsibilities: [
        "Design, develop, and customize Odoo modules and applications as per business needs",
        "Integrate Odoo with third-party tools and services for seamless workflows",
        "Debug, test, and troubleshoot technical issues to ensure optimal performance",
        "Collaborate with functional consultants and developers to deliver high-quality solutions",
        "Stay up to date with the latest Odoo framework updates and best practices",
      ],
      skills: [
        "Odoo Framework",
        "Python",
        "PostgreSQL",
        "Core Odoo Modules (Manufacturing, Inventory, Accounting, HR, etc.)",
        "JavaScript",
        "XML",
        "QWeb",
        "Debugging & Troubleshooting",
        "System Integration",
        "Analytical & Problem-Solving Skills",
      ],
      status: "Active",
    },
    {
      id: "ODOOERP26",
      title: "Odoo ERP Consultant",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education: "Bachelor’s Degree in any discipline / MBA preferred",
      description:
        "We are seeking a highly skilled Odoo ERP Consultant with 3 to 5 years of experience to join our team. The ideal candidate will have strong expertise in implementing, customizing, and supporting Odoo ERP solutions across different business domains. The consultant will act as a bridge between business needs and technical execution, ensuring smooth deployment and optimized use of Odoo modules.",
      requirements:
        "3–5 years of proven experience as an Odoo Functional Consultant or similar role. Strong knowledge of Odoo ERP modules and business workflows. Hands-on experience with Odoo implementation, configuration, and customization. Excellent analytical, problem-solving, and communication skills.",
      jobDescription:
        "As an Odoo ERP Consultant at TISSA Technology, you will analyze client requirements, configure and customize Odoo modules, and ensure smooth implementation. You will collaborate with development teams, conduct system testing, support end-users, and provide ongoing improvements for ERP automation.",
      responsibilities: [
        "Analyze client requirements and translate them into Odoo ERP solutions",
        "Configure, customize, and implement Odoo modules (Sales, Purchase, Inventory, Accounting, HR, CRM, Manufacturing, etc.)",
        "Provide functional support, training, and documentation to end-users",
        "Collaborate with development teams to design custom features and workflows",
        "Conduct system testing, troubleshoot issues, and ensure seamless integration",
        "Assist in data migration, system upgrades, and post-implementation support",
        "Work closely with clients to improve business processes through ERP automation",
      ],
      skills: [
        "Odoo ERP Modules",
        "Business Workflow Analysis",
        "Implementation & Configuration",
        "Customization",
        "Functional Support & Training",
        "System Testing & Troubleshooting",
        "Data Migration",
        "ERP Automation",
        "Analytical & Problem-Solving Skills",
        "Communication & Client Interaction",
      ],
      status: "Active",
    },
    {
      id: "SOLARCH26",
      title: "Solution Architect",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education:
        "Bachelor’s in Computer Science, IT, or related field (MBA/Certifications preferred)",
      description:
        "We are seeking a Solution Architect to design and oversee the implementation of innovative, scalable, and secure technology solutions. The ideal candidate will combine technical expertise, business acumen, and leadership skills to deliver solutions that align with organizational goals and ensure seamless integration across systems and platforms.",
      requirements:
        "Minimum 5 years of proven experience in solution architecture, system design, or technical leadership with at least 3 years in an architect/lead role. Strong knowledge of cloud platforms (AWS, Azure, GCP), enterprise systems, databases, and integration frameworks. Experience in API design, microservices, DevOps, and CI/CD pipelines. Excellent problem-solving, communication, and stakeholder management skills.",
      jobDescription:
        "As a Solution Architect at TISSA Technology, you will collaborate with stakeholders to design end-to-end solutions, create architectural blueprints, and guide technical teams in building scalable, secure systems. You will evaluate emerging technologies, ensure compliance and performance standards, and support digital transformation initiatives across the organization.",
      responsibilities: [
        "Collaborate with business stakeholders to capture requirements and translate them into technical designs",
        "Develop and maintain architecture blueprints, roadmaps, and standards",
        "Design end-to-end solutions covering applications, data, cloud, and infrastructure",
        "Lead technical teams through solution design, integration, and deployment phases",
        "Evaluate emerging technologies and recommend adoption strategies",
        "Ensure solutions meet security, scalability, performance, and compliance requirements",
        "Provide guidance during system upgrades, migrations, and digital transformation initiatives",
        "Act as the technical advisor for pre-sales, proposals, and client interactions",
      ],
      skills: [
        "Solution Architecture",
        "System Design",
        "Cloud Platforms (AWS, Azure, GCP)",
        "Enterprise Systems & Databases",
        "Integration Frameworks",
        "API Design & Microservices",
        "DevOps & CI/CD Pipelines",
        "Technical Leadership",
        "Problem-Solving & Communication",
        "Stakeholder Management",
      ],
      status: "Active",
    },
    {
      id: "WEBDEV26",
      title: "Web Developer (React JS)",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education:
        "Bachelor’s Degree in Computer Science, Information Technology, or related field",
      description:
        "We are looking for a skilled Web Developer with strong expertise in React.js to join our dynamic development team. The ideal candidate should have hands-on experience in building responsive and high-performing web applications. Experience with Node.js and Angular will be considered an added advantage.",
      requirements:
        "2+ years of hands-on experience in web application development. Proficiency in React.js, JavaScript (ES6+), HTML5, and CSS3. Experience with state management libraries such as Redux or Context API. Familiarity with RESTful APIs and version control systems like Git. Strong problem-solving skills and attention to detail.",
      jobDescription:
        "As a Web Developer (React JS) at TISSA Technology, you will be responsible for developing and maintaining high-quality, responsive web applications. You will collaborate closely with UI/UX designers and backend developers to create seamless user experiences while ensuring application performance and scalability. You’ll also contribute to code reviews, troubleshoot issues, and stay updated with emerging web technologies.",
      responsibilities: [
        "Develop, test, and maintain responsive web applications using React.js",
        "Collaborate with UI/UX designers and backend developers to deliver seamless user experiences",
        "Optimize applications for maximum performance and scalability",
        "Write clean, maintainable, and well-documented code following best practices",
        "Troubleshoot and debug application issues across multiple browsers and devices",
        "Participate in code reviews and contribute to continuous improvement initiatives",
        "Stay updated with emerging technologies and frameworks to enhance development efficiency",
      ],
      skills: [
        "React.js",
        "JavaScript (ES6+)",
        "HTML5 & CSS3",
        "Redux / Context API",
        "RESTful API Integration",
        "Git Version Control",
        "Performance Optimization",
        "Troubleshooting & Debugging",
        "UI/UX Collaboration",
        "Node.js / Express.js (Preferred)",
        "Angular / TypeScript / Next.js (Preferred)",
        "CI/CD & Cloud Deployment (Preferred)",
      ],
      status: "Active",
    },
    {
      id: "RNDEV26",
      title: "React Native Developer",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education:
        "Bachelor’s Degree in Computer Science, Engineering, or related field",
      description:
        "We are seeking a skilled React Native Developer to join our dynamic development team. The ideal candidate will be responsible for designing, developing, and maintaining high-quality mobile applications for both Android and iOS platforms. This role requires a strong understanding of mobile development principles, UI/UX standards, and integration with RESTful APIs and third-party libraries.",
      requirements:
        "2+ years of proven experience in React Native development. Strong proficiency in JavaScript and TypeScript. Experience with React Hooks, Redux, or MobX for state management. Familiarity with native build tools like Xcode, Android Studio, and Gradle. Strong debugging and problem-solving skills, along with a good understanding of Git version control and agile methodologies.",
      jobDescription:
        "As a React Native Developer at TISSA Technology, you will be responsible for building and maintaining high-quality, cross-platform mobile applications. You will collaborate with designers, backend developers, and product managers to deliver new features and optimize performance. You’ll also participate in code reviews, integrate APIs, and stay updated with the latest mobile development trends.",
      responsibilities: [
        "Develop and maintain cross-platform mobile applications using React Native",
        "Collaborate with designers, backend developers, and product managers to define and deliver new features",
        "Optimize applications for maximum speed, performance, and scalability",
        "Integrate mobile applications with backend services (REST APIs, GraphQL, etc.)",
        "Debug and fix issues to improve performance and user experience",
        "Participate in code reviews and contribute to continuous improvement in coding standards",
        "Stay updated with the latest industry trends, technologies, and best practices in mobile development",
        "Work closely with QA teams to ensure smooth testing and deployment",
      ],
      skills: [
        "React Native",
        "JavaScript & TypeScript",
        "React Hooks",
        "Redux / MobX",
        "REST API & GraphQL Integration",
        "Xcode / Android Studio / Gradle",
        "Mobile App Architecture & Lifecycle",
        "UI/UX Implementation",
        "Debugging & Performance Optimization",
        "Git Version Control",
        "Agile Development Methodologies",
      ],
      status: "Active",
    },
    {
      id: "TLPA26",
      title: "Technical Lead – Python & Solution Architecture",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education:
        "Bachelor’s in Computer Science, IT, or related field (Certifications preferred)",
      description:
        "We are looking for a Technical Lead – Python & Solution Architecture to drive the architecture, development, and delivery of scalable software solutions. The ideal candidate will combine strong Python backend expertise with solution architecture skills, technical leadership, and client-facing experience in a consulting-driven environment.",
      requirements:
        "5–8 years of professional experience in Python development with a strong background in backend systems and solution architecture. Proven experience leading teams, mentoring developers, and handling client-facing technical discussions. Hands-on experience with modern Python frameworks, databases, cloud platforms, and DevOps practices.",
      jobDescription:
        "As a Technical Lead – Python & Solution Architecture at TISSA Technology, you will design robust system architectures, lead hands-on development, and ensure high-quality delivery. You will collaborate closely with stakeholders, guide engineering teams, participate in client meetings, and contribute to technical proposals, RFPs, and Proof of Concepts (PoCs).",
      responsibilities: [
        "Design and own end-to-end solution architecture for Python-based applications",
        "Lead hands-on development using Python frameworks such as FastAPI, Django, or Flask",
        "Translate business and functional requirements into technical designs and implementation plans",
        "Guide and mentor junior developers through code reviews and technical coaching",
        "Ensure best practices for scalability, security, performance, and maintainability",
        "Act as a technical point of contact for customers during discussions and delivery",
        "Participate in client meetings to explain solution designs and technical trade-offs",
        "Contribute to RFP responses, technical estimations, and architecture documentation",
        "Design and deliver Proof of Concepts (PoCs) to validate technical feasibility and integrations",
      ],
      skills: [
        "Python Development",
        "FastAPI / Django / Flask",
        "Solution Architecture & Design Patterns",
        "REST API Development",
        "PostgreSQL / MySQL / MongoDB",
        "Cloud Platforms (AWS, Azure, GCP)",
        "Docker & CI/CD Pipelines",
        "DevOps Fundamentals",
        "Technical Leadership",
        "Client Communication & Stakeholder Management",
      ],
      status: "Active",
    },
    {
      id: "FSDRN26",
      title: "Full Stack Developer (React & NestJS)",
      company: "TISSA Technology",
      location: "Pune, IN (Onsite)",
      type: "Full-time",
      education: "Bachelor’s in Computer Science, IT, or related field",
      description:
        "We are looking for a skilled Full Stack Developer to design, develop, and maintain modern web applications using React 18 on the frontend and NestJS on the backend. The ideal candidate will have strong TypeScript expertise and a passion for building scalable, high-quality user experiences.",
      requirements:
        "Strong hands-on experience with React 18 and TypeScript. Experience building backend services using NestJS and Express. Solid understanding of modern frontend and backend development practices, including API design, performance optimization, and code quality standards.",
      jobDescription:
        "As a Full Stack Developer (React & NestJS) at TISSA Technology, you will be responsible for developing end-to-end web applications and collaborating closely with product, design, and QA teams. You will work on building responsive user interfaces, scalable backend services, and well-documented APIs while ensuring performance, security, and maintainability.",
      responsibilities: [
        "Develop responsive and high-performance user interfaces using React 18, TypeScript, Vite, and Tailwind CSS",
        "Implement state management using Zustand and build accessible UI components with Headless UI",
        "Integrate advanced UI features such as Monaco Editor and dynamic routing using React Router",
        "Build scalable and secure backend services using NestJS, Express, and TypeScript",
        "Design, document, and consume REST APIs using Swagger",
        "Implement request validation and ensure data integrity using Class Validator",
        "Collaborate with product, design, and QA teams to deliver end-to-end solutions",
        "Write clean, maintainable, and well-documented code",
      ],
      skills: [
        "React 18 & TypeScript",
        "NestJS & Express",
        "Vite & Tailwind CSS",
        "REST API Development",
        "Swagger API Documentation",
        "Class Validator",
        "Zustand State Management",
      ],
      status: "Active",
    },
    {
      id: "SPD26",
      title: "Senior Python Developer",
      company: "TISSA Technology",
      location: "Pune, India (Onsite)",
      type: "Full-time",
      education: "Bachelor’s in Computer Science, IT, or related field",
      description:
        "We are seeking an experienced Senior Python Developer to lead the design and development of scalable backend systems and APIs. The ideal candidate will mentor junior developers, contribute to architecture decisions, and deliver high-quality, production-ready solutions.",
      requirements:
        "5+ years of professional experience in Python development with strong expertise in backend systems and API development. Proven ability to guide teams, lead code reviews, and contribute to technical architecture and design decisions.",
      jobDescription:
        "As a Senior Python Developer at TISSA Technology, you will play a key role in building and maintaining robust backend services. You will collaborate with cross-functional teams, mentor junior engineers, lead code reviews, and ensure solutions meet performance, scalability, and security standards.",
      responsibilities: [
        "Design, develop, and maintain scalable backend applications using Python",
        "Build and optimize RESTful APIs using FastAPI, Django, or Flask",
        "Lead code reviews and ensure best practices for coding, performance, and security",
        "Collaborate with frontend, DevOps, and product teams to deliver integrated solutions",
        "Mentor junior developers and support technical decision-making",
        "Troubleshoot performance issues and resolve production incidents",
      ],
      skills: [
        "Python Development (5+ Years)",
        "FastAPI / Django / Flask",
        "REST API & Microservices Architecture",
        "PostgreSQL / MySQL / MongoDB",
        "Git & CI/CD Pipelines",
        "Cloud Platforms (AWS, Azure, GCP)",
        "Problem-Solving & Communication Skills",
      ],
      status: "Active",
    },
    {
      id: "JPD26",
      title: "Junior Python Developer",
      company: "TISSA Technology",
      location: "Pune, India (Onsite)",
      type: "Full-time",
      education:
        "Bachelor’s in Computer Science, IT, or related field (or pursuing)",
      description:
        "We are looking for a motivated Junior Python Developer to support the development of backend applications and APIs. This role is ideal for freshers or early-career professionals eager to grow their skills while working on real-world projects under the guidance of experienced developers.",
      requirements:
        "0–2 years of experience in Python development (freshers welcome) with a basic understanding of backend development concepts. Strong willingness to learn, adapt, and grow in a professional development environment.",
      jobDescription:
        "As a Junior Python Developer at TISSA Technology, you will assist in building and maintaining backend features, support API development, and help improve existing applications. You will collaborate closely with senior developers, gain hands-on experience, and learn industry best practices.",
      responsibilities: [
        "Assist in developing backend features using Python",
        "Support REST API development and third-party integrations",
        "Write clean, readable, and well-documented code",
        "Fix bugs and support application enhancements",
        "Collaborate with senior developers and learn best practices",
      ],
      skills: [
        "Python (0–2 Years)",
        "Flask / Django / FastAPI (Basic)",
        "REST API Fundamentals",
        "MySQL / PostgreSQL (Basic)",
        "Git & Version Control Basics",
        "Problem-Solving & Learning Mindset",
      ],
      status: "Active",
    },
    {
      id: "USITR26",
      title: "US IT Recruiter",
      company: "TISSA Technology",
      location: "Pune, IN (Remote)",
      type: "Full-time",
      education: "Bachelor's degree or equivalent (preferred)",
      description:
        "We are looking for an enthusiastic and experienced US IT Recruiter to join our team. The ideal candidate should have hands-on experience in the US recruitment process, sourcing IT professionals, coordinating interviews, and working with clients/vendors to fulfill hiring requirements.",
      requirements:
        "1–3 years of proven experience in US IT recruitment with strong expertise in sourcing, interviewing, and onboarding IT professionals.",
      jobDescription:
        "As a US IT Recruiter at STISSA Technology LLP (TISSA Technology), you will be responsible for identifying and attracting top talent in the US IT market. You will collaborate with hiring managers and stakeholders to understand requirements and execute effective recruitment strategies.",
      responsibilities: [
        "Handle end-to-end US IT recruitment process",
        "Source candidates through job portals like Dice, Monster, CareerBuilder, LinkedIn, etc.",
        "Screen, shortlist, and evaluate candidates based on client requirements",
        "Coordinate interviews between candidates and clients",
        "Stay up to date with the latest Odoo framework updates and best practices",
        "Work on W2, C2C, and 1099 requirements",
        "Maintain regular follow-up with candidates and clients",
        "Negotiate salary/rates and manage offer discussions",
        "Maintain recruitment trackers and daily activity reports",
        "Build and maintain a strong candidate pipeline",
        "Ensure timely closures of open positions",
      ],
      skills: [
        "1–3 years of experience in US IT Recruitment / US Staffing",
        "Strong understanding of US recruitment lifecycle",
        "Knowledge of US tax terms: W2, C2C, and 1099",
        "Hands-on experience with ATS tools and job portals",
        "Excellent communication and interpersonal skills",
        "Ability to work in a fast-paced and target-driven environment",
        "Good sourcing and screening skills",
      ],
      status: "Active",
    }, 
    // Add more job objects as needed
  ];

  const [formErrors, setFormErrors] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    resume: null,
  });
  const [submitted, setSubmitted] = useState(false);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Full Name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = "Phone must be 10 digits";
    }

    if (!formData.resume) {
      errors.resume = "Resume is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // Clear the error for the field being modified
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: name === "resume" ? files[0] : value,
    }));
  };

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobile", formData.phone);
      formDataToSend.append("position", selectedJob?.title || "N/A");
      formDataToSend.append("resume", formData.resume);
      formDataToSend.append("location", selectedJob?.location);

      const response = await fetch(
        "https://tissa-api.tissatech.in/sendmail.php",
        {
          method: "POST",
          body: formDataToSend,
        }
      );
      console.log(response);

      const result = await response.json();
      if (result.status === "ok") {
        setLoading(false);
        console.log("Mail sent:", result.message);
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setIsVisible(false);
        }, 7000);
      } else {
        alert("Failed to send application. Please try again.");
      }
    } catch (error) {
      console.error("Error sending mail:", error);
      console.log("Error sending mail:", error);
      alert("Something went wrong. Please try again.", error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleOpenPopup = (job) => {
    setSelectedJob(job);
    setIsVisible(true);
    setFormData({
      name: "",
      email: "",
      phone: "",
      resume: null,
    });
    setFormErrors({});
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;
  const totalPages = Math.ceil(jobList.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = jobList.slice(startIndex, endIndex);

  return (
    <div>
      <HeroSection
        title="Current Openings - India"
        breadcrumb={[
          { name: "Career", url: "/career" },
          { name: "India Careers Page" },
        ]}
        background="/assets/images/career.jpg"
      />

      {/* Popup */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
          isVisible ? "block" : "hidden"
        }`}
      >
        <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-md relative">
          <button
            onClick={handleClose}
            className="absolute top-2 right-3 text-2xl text-gray-600 hover:text-red-600"
            aria-label="Close"
          >
            &times;
          </button>

          {selectedJob && (
            <h2 className="text-xl font-semibold mb-4">
              {selectedJob.title}: {selectedJob.id}
            </h2>
          )}

          {submitted ? (
            <div className="text-green-700 bg-green-100 p-4 rounded">
              🎉 Thank you for applying! We'll contact you shortly.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mt-5 font-medium">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mt-5 font-medium">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md"
                  maxLength={10}
                />
                {formErrors.phone && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm mt-5 font-medium">
                  Resume *
                </label>
                <input
                  type="file"
                  name="resume"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx"
                  className="w-full"
                />
                {formErrors.resume && (
                  <p className="text-red-500 text-xs absolute">
                    {formErrors.resume}
                  </p>
                )}
              </div>

              <br />

              {!loading ? (
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
                >
                  Submit Application
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full bg-blue-300 text-white py-2 rounded-md"
                >
                  Submiting Application...
                </button>
              )}
            </form>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto my-8 p-6 rounded-lg">
        {/* Header Section */}
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-4"></h1>
        <div className="flex justify-center items-center mb-2">
          <Briefcase className="w-7 h-7 text-gray-600" />
        </div>
        <p className="text-center text-gray-600 mb-8">
          Explore our current job opportunities and take the next step in your
          career with TISSA Technology.
        </p>

        {/* Job Cards */}
        {currentJobs.map((job, index) => (
          <div
            key={job.id}
            ref={(el) => (jobRefs.current[job.id] = el)}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-lg transition-all duration-300 mb-6"
          >
            <div
              className="flex items-start justify-between p-6 cursor-pointer"
              onClick={() => toggleDetails(job.id)}
            >
              {/* Job Details */}
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {job.title}{" "}
                  <span className="text-sm text-gray-500 mx-2">
                    ID: {job.id}
                  </span>
                </h2>
                <div className="flex items-center text-sm text-gray-600 space-x-2 mt-1">
                  <div className="flex min-w-[140px]">
                    <span>
                      <HiOfficeBuilding className="w-4 h-4 mr-1" />
                    </span>
                    {job.company}
                  </div>

                  <div className="flex min-w-[140px]">
                    <span>
                      <HiMapPin className="w-4 h-4 mx-1" />
                    </span>
                    {job.location}
                  </div>

                  <div className="flex min-w-[100px]">
                    <span>
                      <HiCalendar className="w-4 h-4 mx-1" />
                    </span>
                    {job.type}
                  </div>

                  <div className="flex">
                    <span>
                      <HiAcademicCap className="w-4 h-4 mx-1" />
                    </span>
                    {job.education}
                  </div>
                </div>
                <p className="mt-2 text-gray-600">{job.description}</p>
              </div>
              {/* Status and Toggle Icon */}
              <div className="flex flex-col items-end">
                <span className="flex items-center space-x-1 text-green-900 bg-green-200 text-sm px-2 py-1 rounded-xl mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-bold">{job.status}</span>
                </span>
                {openJobId === job.id ? (
                  <ChevronUp className="w-6 h-6 text-gray-700" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-700" />
                )}
              </div>
            </div>

            {/* Expanded Section */}
            {openJobId === job.id && (
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                {/* Requirements */}
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Requirements
                </h3>
                <p className="text-gray-700 mb-4">{job.requirements}</p>

                {/* job Description */}
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Job Description
                </h3>
                <p className="text-gray-700 mb-4 text-justify">
                  {job.jobDescription}
                </p>

                {/* Responsibilities */}
                <h3 className="text-lg font-bold mb-2">Responsibilities</h3>
                <ul className="list-none space-y-2 mb-4">
                  {job.responsibilities.map((item, i) => (
                    <li key={i} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Skills */}
                <h3 className="text-lg font-bold mb-2">Required Skills</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {job.skills.map((skill, i) => (
                    <span
                      key={i}
                      className="text-sm font-medium text-gray-700 bg-gray-200 px-2 py-1 rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleOpenPopup(job)}
                    className="inline-block bg-primary hover:bg-primary-dark text-black border font-medium py-2 px-6 rounded-lg transition-colors duration-300"
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Pagination Controls */}
        {jobList?.length > 5 && (
          <div className="flex justify-center items-center mt-6 space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border bg-green-300 rounded disabled:opacity-50"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentPage === i + 1 ? "bg-blue-600 text-white" : "bg-white"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 border bg-green-300 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        <div className="max-w-6xl mx-auto my-8">
          {/* Application Process Section */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Application Process
            </h2>
            <p className="text-gray-600 mb-4">
              To apply for any of the positions listed above, please mail your
              resume with the job ID # to our HR department:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg text-gray-700 mb-4">
              <p className="font-bold">TISSA Technology</p>
              <p>Human Resources Department</p>
              <p>Unit 1018, Gera Imperium Rise, Wipro Circle, Hinjawadi</p>
              <p>Pune, Maharashtra, IN 411057</p>
            </div>
            {/* <p className="text-sm text-gray-500">
              <strong>Note:</strong> Unanticipated work site locations
              throughout U.S. Foreign equivalency accepted.
            </p> */}
          </div>

          {/* Contact Us and Location Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            {/* Contact Us */}
            <div className="bg-white shadow rounded-lg p-6 flex flex-col items-center text-center">
              <HiMail className="w-6 h-6 text-gray-600 mb-2" />
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Contact Us
              </h3>
              <p className="text-gray-600 mb-4">
                Have questions about our current openings? Feel free to reach
                out to our HR team.
              </p>
              <a
                href="mailto:careers@tissatech.com"
                className="text-primary font-medium"
              >
                careers@tissatech.com
              </a>
            </div>

            {/* Our Location */}
            <div className="bg-white shadow rounded-lg p-6 flex flex-col items-center text-center">
              <HiMapPin className="w-6 h-6 text-gray-600 mb-2" />
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Our Location
              </h3>
              <p className="text-gray-600 mb-4">
                TISSA Technology <br />
                Office no. 505, City Avenue, Oppo. Phoenix Mall of the Millennium,
                <br />
                Mumbai-Bangalore Highway, Wakad, Pune - 411057
              </p>
            </div>
          </div>

          <WhyJoinTissa />
        </div>
      </div>
    </div>
  );
};

export default IndiaJobOpening;
